import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, List, ListItem, ListItemText, Paper, TableContainer, Tooltip,
  InputAdornment, Divider, Slide, Skeleton, CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Delete, Visibility, Search as SearchIcon, Save, Close,
  ChevronLeft, ChevronRight
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const TransitionUp = React.forwardRef(function TransitionUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ===== utils =====
const formatBRL = (n) => {
  const val = Number(n);
  if (!Number.isFinite(val)) return '—';
  try { return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }
  catch { return `R$ ${val.toFixed(2)}`; }
};
const safeNumber = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const Entradas = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    fornecedor: null,
    q: '',
  });
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [total, setTotal] = useState(0);

  const [fornecedorOptions, setFornecedorOptions] = useState([]);
  const [insumoOptions, setInsumoOptions] = useState([]);
  const [fornLoading, setFornLoading] = useState(false);
  const [insumoLoading, setInsumoLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fornecedor: null,
    data: new Date(),
    itens: [],
  });

  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [currentItems, setCurrentItems] = useState([]);

  const valorNota = useMemo(
    () => form.itens.reduce((sum, i) => sum + safeNumber(i.subtotal), 0),
    [form.itens]
  );

  // ===== fetch list =====
  const fetchEntradas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        q: filters.q || undefined,
        fornecedor_id: filters.fornecedor?.id,
        date_from: filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : undefined,
        date_to: filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : undefined,
      };
      const { data } = await api.get('/confereai/entradas', { params });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setEntries(list);
      setTotal(data.total ?? data.meta?.total ?? list.length);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar entradas');
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchEntradas(); }, [fetchEntradas]);

  // ===== preload fornecedores ao montar (filtro + modal) =====
  useEffect(() => {
    handleFornecedorSearch('');   // carrega lista inicial
    handleInsumoSearch('');       // opcional: já deixa itens prontos no modal
  }, []);

  // ===== search helpers =====
  const handleFornecedorSearch = async (value) => {
    setFornLoading(true);
    try {
      const { data } = await api.get('/confereai/fornecedores', {
        params: { q: value || undefined, per_page: 100 },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setFornecedorOptions(list);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao buscar fornecedores');
    } finally {
      setFornLoading(false);
    }
  };

  const handleInsumoSearch = async (value) => {
    setInsumoLoading(true);
    try {
      const { data } = await api.get('/confereai/insumos', {
        params: { q: value || undefined, per_page: 100 },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setInsumoOptions(list);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao buscar insumos');
    } finally {
      setInsumoLoading(false);
    }
  };

  // ===== modal new entry =====
  const handleOpenModal = () => {
    setForm({ fornecedor: null, data: new Date(), itens: [] });
    setModalOpen(true);
  };

  const addItemRow = () => {
    setForm((f) => ({
      ...f,
      itens: [...f.itens, { insumo: null, quantidade: '', subtotal: '' }],
    }));
  };

  const handleSave = async () => {
    if (!form.fornecedor) {
      toast.warning('Selecione o fornecedor');
      return;
    }
    const itensPayload = form.itens
      .filter((i) => i.insumo && safeNumber(i.quantidade) >= 0 && safeNumber(i.subtotal) >= 0)
      .map((i) => ({
        insumo_id: i.insumo.id,
        quantidade: safeNumber(i.quantidade),
        subtotal: safeNumber(i.subtotal),
      }));
    if (itensPayload.length === 0) {
      toast.warning('Adicione ao menos um item à nota.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        fornecedor_id: form.fornecedor.id,
        data: format(form.data, 'yyyy-MM-dd'),
        valor_nota: itensPayload.reduce((s, it) => s + it.subtotal, 0)
      };
      const { data } = await api.post('/confereai/entradas', payload);
      const entradaId = data.id;
      await api.post(`/confereai/entradas/${entradaId}/itens`, itensPayload);
      toast.success('Entrada registrada');
      setModalOpen(false);
      fetchEntradas();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar entrada');
    } finally {
      setSaving(false);
    }
  };

  // ===== row actions =====
  const handleDelete = async (row) => {
    if (!window.confirm('Excluir entrada?')) return;
    try {
      await api.delete(`/confereai/entradas/${row.id}`);
      toast.success('Entrada excluída');
      fetchEntradas();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir entrada');
    }
  };

  const handleViewItems = async (row) => {
    try {
      const { data } = await api.get(`/confereai/entradas/${row.id}/itens`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setCurrentItems(list);
      setItemsDialogOpen(true);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar itens');
    }
  };

  // ===== pagination =====
  const totalPages = Math.ceil(total / perPage) || 1;

  // ===== custom calendar header (alinha setas no topo) =====
  const renderCalendarHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Box sx={{
      px: 1.5, pt: 1, pb: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    }}>
      <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled} size="small" aria-label="Mês anterior">
        <ChevronLeft fontSize="small" />
      </IconButton>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
        {format(date, "MMMM yyyy", { locale: pt })}
      </Typography>
      <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled} size="small" color="primary" aria-label="Próximo mês">
        <ChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );

  // ===== UI =====
  return (
    <Box sx={{ px: { xs: 3, sm: 5 }, pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3, py: 1.5, px: 0.5,
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 0.3
          }}
        >
          Entradas
        </Typography>

        <Button variant="contained" startIcon={<Add />} onClick={handleOpenModal}>
          Nova Nota
        </Button>
      </Box>

      {/* Filtros */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        elevation={0}
        sx={{
          p: 2.5, mb: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.7))',
          backdropFilter: 'blur(8px)'
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} sm={3}>
            <DatePicker
              selected={filters.dateFrom}
              onChange={(d) => { setFilters((f) => ({ ...f, dateFrom: d })); setPage(1); }}
              customInput={<TextField fullWidth label="Data inicial" />}
              dateFormat="dd/MM/yyyy"
              locale={pt}
              renderCustomHeader={renderCalendarHeader}
              showPopperArrow={false}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              selected={filters.dateTo}
              onChange={(d) => { setFilters((f) => ({ ...f, dateTo: d })); setPage(1); }}
              customInput={<TextField fullWidth label="Data final" />}
              dateFormat="dd/MM/yyyy"
              locale={pt}
              renderCustomHeader={renderCalendarHeader}
              showPopperArrow={false}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={fornecedorOptions}
              loading={fornLoading}
              getOptionLabel={(o) => o?.nome || ''}
              value={filters.fornecedor}
              onInputChange={(e, v) => handleFornecedorSearch(v)}
              onChange={(e, v) => { setFilters((f) => ({ ...f, fornecedor: v })); setPage(1); }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fornecedor"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {fornLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Buscar"
              fullWidth
              value={filters.q}
              onChange={(e) => { setFilters((f) => ({ ...f, q: e.target.value })); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          background: '#fff'
        }}
      >
        <Table
          size="small"
          sx={{
            '& thead th': { bgcolor: 'grey.50', fontWeight: 600 },
            '& th, & td': { px: 2 },
            '& th:first-of-type, & td:first-of-type': { pl: 3 },
            '& th:last-of-type,  & td:last-of-type':  { pr: 3 },
            '& thead th:first-of-type': { borderTopLeftRadius: 12 },
            '& thead th:last-of-type':  { borderTopRightRadius: 12 },
            '& tbody tr:hover': { bgcolor: 'action.hover' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Data lançamento</TableCell>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Valor da nota</TableCell>
              <TableCell width={110} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence initial={false}>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell><Skeleton height={20} width="70%" /></TableCell>
                    <TableCell><Skeleton height={20} width="40%" /></TableCell>
                    <TableCell><Skeleton height={20} width="50%" /></TableCell>
                    <TableCell><Skeleton height={20} width="60%" /></TableCell>
                    <TableCell align="right"><Skeleton height={24} width={80} /></TableCell>
                  </TableRow>
                ))
              ) : entries.length ? (
                entries.map((row) => (
                  <TableRow
                    key={row.id}
                    component={motion.tr}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                    hover
                  >
                    <TableCell>{row.data ? format(new Date(row.data), 'dd/MM/yyyy') : '—'}</TableCell>
                    <TableCell>{row.fornecedor?.nome || row.fornecedor_nome || '—'}</TableCell>
                    <TableCell>{formatBRL(row.valor_nota)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver itens">
                        <IconButton size="small" onClick={() => handleViewItems(row)} aria-label="Ver itens">
                          <Visibility fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(row)} aria-label="Excluir entrada">
                          <Delete fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Nenhuma entrada encontrada</Typography>
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Box px={2}>{page} / {totalPages}</Box>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
        </Box>
      )}

      {/* Modal criação */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 16, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 16, scale: 0.98 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 4, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Nova Entrada
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={fornecedorOptions}
                loading={fornLoading}
                getOptionLabel={(o) => o?.nome || ''}
                value={form.fornecedor}
                onInputChange={(e, v) => handleFornecedorSearch(v)}
                onChange={(e, v) => setForm({ ...form, fornecedor: v })}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fornecedor"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {fornLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                selected={form.data}
                onChange={(d) => setForm({ ...form, data: d })}
                customInput={<TextField fullWidth label="Data de lançamento" />}
                dateFormat="dd/MM/yyyy"
                locale={pt}
                renderCustomHeader={renderCalendarHeader}
                showPopperArrow={false}
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>Itens</Typography>

            {form.itens.map((item, idx) => (
              <Grid container spacing={1.5} alignItems="center" key={idx} sx={{ mb: 0.5 }}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={insumoOptions}
                    loading={insumoLoading}
                    getOptionLabel={(o) => o?.nome || ''}
                    value={item.insumo}
                    onInputChange={(e, v) => handleInsumoSearch(v)}
                    onChange={(e, v) => {
                      const itens = [...form.itens];
                      itens[idx].insumo = v;
                      setForm({ ...form, itens });
                    }}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Insumo"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {insumoLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Qtd"
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    value={item.quantidade}
                    onChange={(e) => {
                      const itens = [...form.itens];
                      itens[idx].quantidade = e.target.value;
                      setForm({ ...form, itens });
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Subtotal"
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    value={item.subtotal}
                    onChange={(e) => {
                      const itens = [...form.itens];
                      itens[idx].subtotal = e.target.value;
                      setForm({ ...form, itens });
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={1} display="flex" justifyContent="flex-end">
                  <Tooltip title="Remover">
                    <IconButton
                      onClick={() =>
                        setForm((f) => ({ ...f, itens: f.itens.filter((_, i) => i !== idx) }))
                      }
                      size="small"
                      aria-label="Remover item"
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
              <Button startIcon={<Add />} onClick={addItemRow}>
                Adicionar item
              </Button>
              <Typography sx={{ fontWeight: 700 }}>
                Total: {formatBRL(valorNota)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Close />} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button
            startIcon={saving ? <CircularProgress size={18} /> : <Save />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Itens Dialog */}
      <Dialog
        open={itemsDialogOpen}
        onClose={() => setItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 16 },
          transition: { duration: 0.18 },
          sx: { borderRadius: 4, overflow: 'hidden' }
        }}
      >
        <DialogTitle>Itens da entrada</DialogTitle>
        <Divider />
        <DialogContent dividers>
          <List dense>
            {currentItems.map((item) => (
              <ListItem key={item.id} sx={{ px: 0 }}>
                <ListItemText
                  primary={item.nome || item.insumo_nome || '—'}
                  secondary={`Qtd: ${item.quantidade}  •  Subtotal: ${formatBRL(item.subtotal)}`}
                />
              </ListItem>
            ))}
            {currentItems.length === 0 && (
              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="Nenhum item" />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setItemsDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Entradas;
